import {
  FC,
  Key,
  useState,
  useEffect,
  MutableRefObject,
  useRef,
  HTMLAttributes,
  ReactNode,
  CSSProperties,
  useMemo,
} from 'react';
// 组件
import { CheckboxProps, Table } from 'antd';
import TableToolbar, {
  TableToolbarProps,
  toolBarType,
} from './subpage/tableToolbar';
import TablePagination from './subpage/tablePagination';
import TableSubTotal, { BaseTableSubTotalProps } from './subpage/tableSubTotal';
import TableTotal, {
  BaseTableTotalProps,
  ITotalItem,
} from './subpage/tableTotal';
import SuperForm, { SuperFormItemProps } from '../SuperForm';
import ResizableTitle from './subpage/resizableTitle';
// 方法
import useTableHook from './hook/useTableHook';
import useResizeChange from '../Hooks/useResizeChange';
import { getElementTop } from './../utils/utils';
import classnames from 'classnames';
// 常量
import { TableProps } from 'antd/lib/table';
import { TableTotalActionType } from './subpage/tableTotal';
import { toolbarActionType } from './subpage/tableToolbar';
import { SearchFormOperationProps } from '../SearchForm/operation';
import { PaginationProps } from 'antd/lib/pagination';
import { ColProps } from 'antd/lib/col';
import { TableRowSelection, RowSelectionType, RowSelectMethod } from 'antd/lib/table/interface';
import {
  columnsProps,
  operationType,
  tableSizeType,
  TableActionType,
  draftsType,
  IselectedInfo,
  IGroupField,
} from './interface';
import { IBtnList } from '../ButtonGroup/buttonGroup';
import { FormInstance } from 'antd/lib/form';
import { IObject } from '../constants/interface';
import { BODY_HEIGHT } from '../constants/constant';
import './style.less';

export interface SuperTableProps
  extends Omit<TableToolbarProps, 'size' | 'onRefresh'>,
    BaseTableSubTotalProps,
    BaseTableTotalProps {
  /** 列配置 */
  columns: columnsProps[]; // 列配置
  /** 列表接口api */
  listApi?: (params: any) => Promise<any>; // 列表接口api
  /** 测试ID */
  testId?: string;
  /** 行唯一标识 */
  rowKey?: string | ((record: any) => string); // 行唯一标识
  /** 列数据在数据项中对应的路径 */
  dataKey?: string | false; // 列数据在数据项中对应的路径
  /** 操作列配置 */
  operation?: operationType; // 操作列配置
  /** 操作列下拉底部按钮 */
  operationFooterList?: IBtnList[]; // 操作列下拉底部按钮
  /** 分页配置 */
  pagination?: PaginationProps | false; // 分页配置
  /** 表格布局大小 */
  size?: tableSizeType; // 表格布局大小
  /** 组件对象 */
  actionRef?: MutableRefObject<TableActionType>; // 组件对象
  /** 表格默认搜索参数 */
  defauleSearchParams?: any; // 表格默认搜索参数
  /** 搜索框配置 */
  formItems?: SuperFormItemProps[]; // 搜索框配置
  /** 表格使用场景 */
  sceneType?: 'modal' | 'page'; // 表格使用场景
  /** 列宽是否可以拖动 */
  resizable?: boolean;
  /** 启动虚拟行的最小条数 试用阶段暂不支持小计和总计 */
  minVirtualTotal?: number | false;
  /** 表格行是否可选择配置 */
  rowSelection?: TableRowSelection<any>; // 表格行是否可选择配置
  /** 是否处于loading状态 */
  loading?: boolean;
  /** 表格id */
  tableId?: string; // 表格id;
  /** 是否展示外边框和列边框 */
  bordered?: boolean; // 是否展示外边框和列边框
  /** 搜索操作按钮配置 */
  operationProps?: SearchFormOperationProps; // 搜索操作按钮配置
  /** 总页数 */
  totalKey?: string; // 总页数
  /** 请求延时时间（毫秒），优化频繁触发请求 */
  delay?: number; // 请求延时时间（毫秒），优化频繁触发请求
  /** 搜索框一行默认展示多少个 */
  defaultLineNumber?: number; // 搜索框一行默认展示多少个
  /** 搜索框label配置 */
  defaultLabelCol?: ColProps; // 搜索框label配置
  /** 表格数据 */
  dataSource?: any[]; // 表格数据
  /** 配置展开属性 */
  expandable?: any; // 配置展开属性
  /** 是否显示表头 */
  showHeader?: boolean; // 是否显示表头
  /** 是否自动设置高度 */
  autoHeight?: boolean; // 是否自动设置高度
  /** 自动分页 */
  autoPage?: boolean; // 是否自动分页
  /** 查询框最多展示数量 */
  maxItemLength?: number; // 查询框最多展示数量
  /** 是否隐藏高级查询 */
  highSearch?: boolean; // 是否展示高级查询
  /** 自动计算高度时的最小高度 */
  autoMinHeight?: number;
  /** 自动计算高度时的最大高度 */
  autoMaxHeight?: number;
  /** 是否显示滚动条 */
  hideScroll?: boolean;
  /** 滚动条配置 */
  scroll?: TableProps['scroll'];
  /** 头部提示 */
  headerTip?: ReactNode;
  /** 是否为主子表 */
  isMainSubTable?: boolean;
  /** 是否为合并(行/列)表格 */
  isMergeTable?: boolean;
  /** 是否为合并(行/列)表格中的子表 */
  isMergeChildrenTable?: boolean;
  /** 子表的最小高度 */
  childrenTableMinHeight?: number;
  /** 表格最外层类名 */
  wrapperClassName?: string;
  /** 表格内容样式 */
  wrapperStyle?: CSSProperties;
  /** 表格内容类目 */
  contentClassName?: string;
  /** 表格内容样式 */
  contentStyle?: CSSProperties;
  /** 草稿箱配置 */
  drafts?: draftsType;
  /** 是否显示表格头部搜索框的边框 */
  showSearchFormBorder?: boolean;
  /** 高级搜索宽度 */
  highWidth?: number;
  /** 下拉匹配字段名称 */
  associateSelectNames?: string[] | string;
  /** 组合字段集合 */
  groupFields?: IGroupField[];
  /** 时间类型 */
  dateType?: string;
  /** 选择框类型 */
  rowSelectionType?: RowSelectionType;
  /** 选中信息配置 */
  selectedInfo?: IselectedInfo;
  /** 是否开启奇偶行显示 */
  oddEvenLine?: boolean;
  /** 表格行的类名 */
  rowClassName?: (record: any, index: number) => string;
  /** 点击重置回调 */
  onReset?: (defaultParams?: any) => void;
  /** 对通过 request 获取的数据进行处理 */
  postData?: <T>(data: T[], params: any) => T[]; // 对通过 request 获取的数据进行处理
  /** 获取数据之前参数处理 */
  onBefore?: (params?: any) => any;
  /** 点击操作按钮 */
  onClickOperation?: (key: string | number, record: any, index: number) => void; // 点击操作按钮
  /** 设置行属性 */
  onRow?: (record: any, index?: number) => HTMLAttributes<HTMLElement>; // 设置行属性
  /** 表格工具栏配置 */
  toolBar?: false | toolBarType[];
  /** 表格头部操作栏（区）*/
  headOperation?: false | ReactNode[];
  /** 点击搜索按钮时回调 */
  onBeforeSearch?: ((values: IObject) => IObject | false) | ((values: IObject) => Promise<IObject | false>);
  /** 改变表格大小处理*/
  onChangeSize?: (val: tableSizeType) => void;
  /** 请求完成回调 */
  onFetchComplete?: (data: any[], paginationParams: any) => void;
  /** 请求正常完成后渲染表格之前回调 */
  onFinal?: (data: any[], paginationParams: any) => void;
  /** 刷新之前回调，可用于同步数据*/
  onBeforeRefresh?: (params?: any) => Promise<any>;
  /** 刷新当前页面处理*/
  onRefresh?: (listParams: any, paginationParams: any) => void;
  /** 修改单元格成功 */
  onEditSuccess?: () => void;
  /** 列表搜索条件改变 */
  onSearchChange?: (searchParams: any) => void;
  /** 列表分页切换 */
  onPaginationChange?: (pageParams: any) => void;
  /** 选项改变 */
  onSelect?: (selectedRowKeys?: Key[], selectedRows?: any[]) => void;
  /** 选择框CheckboxProps属性 */
  getCheckboxProps?: (record: any) => Partial<Omit<CheckboxProps, 'checked' | 'defaultChecked'>>;
  /** 总结栏 */
  summary?: (currentData: any) => ReactNode;
  /**需要小计的字段 */
  subTotalKeys?: false | string[];
  /** 小计在哪一列显示 */
  subTotalIndex?: string;
  /** 小计文字 */
  subTotalText?: ReactNode;
  /** 需要总计的字段 */
  totalKeys?: false | ITotalItem[];
  /** 总计在哪一列显示 */
  totalIndex?: string;
  /** 总计文字 */
  totalText?: ReactNode;
  /** 用于统计的api接口 */
  totalApi?: (params?: any) => Promise<any>;
  /** 用于统计的api接口参数 */
  totalApiParams?: any;
  /** 统计数据的key值 */
  totalDataKey?: string;
  /** 底部高度 */
  footerHeight?: number;
  /** 表格内容的下边距 */
  contentPaddingBottom?: number;
  /** 是否隐藏列表内容 */
  hideContainer?: boolean;
  /** 表格行是否开启 hover 交互 */
  rowHoverable?: boolean;
}
/** 表格 */
const SuperTable: FC<SuperTableProps> = props => {
  const {
    rowKey,
    toolBar,
    size,
    formItems,
    actionRef,
    headOperation,
    sceneType,
    rowSelection,
    loading,
    tableId,
    bordered,
    resizable,
    columns: outColumns,
    operationProps,
    defaultLineNumber,
    defaultLabelCol,
    subTotalKeys,
    subTotalIndex,
    subTotalText,
    totalKeys,
    totalIndex,
    totalDataKey,
    headerTip,
    wrapperStyle,
    wrapperClassName,
    contentStyle,
    contentClassName,
    hideScroll,
    scroll,
    copyColumns,
    totalApi,
    totalApiParams,
    totalText,
    expandable,
    showHeader,
    autoHeight,
    maxItemLength = 3,
    highSearch,
    autoMinHeight = 200,
    autoMaxHeight,
    testId = 'table',
    footerHeight = 0,
    drafts,
    showSearchFormBorder,
    totalData,
    contentPaddingBottom,
    defaultUnrepeated,
    highWidth,
    toolBarButtons,
    toolBarFooterContent,
    isMainSubTable,
    isMergeTable,
    rowHoverable,
    hideContainer,
    rowSelectionType,
    selectedInfo,
    showHelpIcon,
    headOperationAfter,
    oddEvenLine,
    isMergeChildrenTable,
    childrenTableMinHeight,
    chilrenKey,
    onBeforeSearch,
    rowClassName,
    onRow,
    onSelect,
    onSelectEmpty,
    onPaginationChange,
    getCheckboxProps,
    summary,
  } = props;
  const tableRef = useRef<any>(null);
  const searchFormRef = useRef<FormInstance>();
  const containerRef = useRef<HTMLDivElement>(null);
  const totalRef = useRef<TableTotalActionType>();
  const toolbarRef = useRef<toolbarActionType>();
  const heightTimer = useRef<any>(); // 设置高度
  const [tableRowSelection, setTableRowSelection] = useState<
    TableRowSelection<any> | undefined
  >(rowSelection);
  const [selectedData, setSelectedData] = useState<IselectedInfo>({
    ...selectedInfo,
  });
  const [tableSize, setTableSize] = useState<tableSizeType>(size || 'middle');
  const [tableHeight, setTableHeight] = useState<number>(autoMaxHeight || 300);
  const {
    loading: intLoading,
    currentDataSource,
    columns,
    pagination,
    total,
    virtual,
    updateDataSource,
    updatePagination,
    updateListParams,
    refreshList,
    resetTable,
    initDable,
    onChangeColumns,
    getTableParams,
    changeLoadingStatus,
    resetPagination,
    // @ts-ignore
  } = useTableHook({ ...props, searchFormRef, totalRef, tableRef });
  // 设置表格高度
  const setSuperTableHeight = (flag: boolean, timer: number = 0) => {
    const el = containerRef.current;
    if (isMergeChildrenTable) {
      if (el && childrenTableMinHeight) {
        const tableEl = el.querySelector('.ant-table-content table') as HTMLDivElement;
        tableEl.style.minHeight = `${childrenTableMinHeight}px`;
      }
      return;
    }
    if (el && flag && autoHeight) {
      let baseBottomHeight: number = sceneType === 'page' ? 58 : 238;
      if (!pagination) baseBottomHeight = baseBottomHeight - 56;
      // 表头高度
      // const tableTitleHeight: number = showHeader ? 39 : 0;
      // 主子表表头高度计算有误，暂时先注释 目前项目都是紧凑型表头可不计算
      // if (showHeader) {
      //   const tableTitleEl = el.querySelector(
      //     '.ant-table-header',
      //   ) as HTMLDivElement;
      //   tableTitleHeight = tableTitleEl?.clientHeight || 39;
      // }
      let tableHeight = 0;
      if (virtual) {
        const virtualEL = el.querySelector('.ant-table-tbody-virtual') as HTMLDivElement;
        tableHeight =
        (document.body.clientHeight || BODY_HEIGHT) -
        getElementTop(virtualEL) -
        baseBottomHeight -
        footerHeight;
      } else {
        const tableEl = el.querySelector('.ant-table-body') as HTMLDivElement;
        tableHeight =
          (document.body.clientHeight || BODY_HEIGHT) -
          getElementTop(tableEl) -
          baseBottomHeight -
          footerHeight;
        if (tableEl) {
          if (sceneType === 'page') {
            const difTableHeight = tableHeight;
            let newHeight = difTableHeight;
            // if (autoMaxHeight) tableEl.style.maxHeight = `${autoMaxHeight}px`;
            if (newHeight < autoMinHeight) newHeight = autoMinHeight;
            if (autoMaxHeight && newHeight > autoMaxHeight)
              newHeight = autoMaxHeight;
            tableEl.style.height = `${newHeight}px`;
          }
          setTimeout(() => {
            if (tableEl.scrollWidth > tableEl.clientWidth) {
              const bbb = el.getElementsByClassName(
                'ant-table-summary',
              )[0] as HTMLDivElement;
              if (bbb) bbb.style.bottom = `10px`;
            }
          }, timer);
          const firstChild = tableEl.firstChild as HTMLTableElement;
          if (contentPaddingBottom) {
            firstChild.style.paddingBottom = `${contentPaddingBottom}px`;
          } else {
            let paddingBottom: number = 0;
            if (totalKeys || subTotalKeys) {
              paddingBottom = 48;
              if (totalKeys && subTotalKeys) paddingBottom = 95;
            }
            firstChild.style.paddingBottom = `${paddingBottom}px`;
          }
        }
      }
      let newTableHeight = tableHeight;
      if (autoMaxHeight && newTableHeight > autoMaxHeight) {
        newTableHeight = autoMaxHeight;
      }
      if (autoMinHeight && newTableHeight < autoMinHeight) {
        newTableHeight = autoMinHeight;
      }
      setTableHeight(autoMaxHeight || newTableHeight);
    }
  };
  // 监听窗口变化
  useResizeChange(() => setSuperTableHeight(true));
  useEffect(() => {
    setSuperTableHeight(true, 200);
  }, []);
  // 初始化表格高度
  useEffect(() => {
    heightTimer.current && clearTimeout(heightTimer.current);
    heightTimer.current = setTimeout(() => {
      setSuperTableHeight(true);
    }, 200);
    if (actionRef && actionRef.current && searchFormRef.current) {
      actionRef.current.form = searchFormRef.current;
    }
    const el = containerRef.current;
    if (!el) return;
    const scrollListener = (event: Event) => {
      const aaa = event.target as HTMLDivElement;
      const bbb = el.getElementsByClassName(
        'ant-table-summary',
      )[0] as HTMLDivElement;
      bbb.scrollLeft = aaa.scrollLeft;
    };
    const tableEl = el.getElementsByClassName(
      'ant-table-body',
    )[0] as HTMLDivElement;
    // if (contentPaddingBottom) {
    //   const firstChild = tableEl.firstChild as HTMLTableElement;
    //   firstChild.style.paddingBottom = `${contentPaddingBottom}px`;
    // }
    // setTimeout(() => {
    //   if (tableEl?.scrollWidth > tableEl?.clientWidth) {
    //     const bbb = el.getElementsByClassName(
    //       'ant-table-summary',
    //     )[0] as HTMLDivElement;
    //     if (bbb) bbb.style.bottom = `10px`;
    //   }
    // }, 200);
    tableEl?.addEventListener('scroll', scrollListener);
    return () => {
      tableEl?.removeEventListener('scroll', scrollListener);
    };
  }, [tableSize, formItems, virtual, columns]);
  useEffect(() => {
    onSelect?.(selectedData.selectedRowKeys, selectedData.selectedRows);
  }, [selectedData.selectedRows]);
  useEffect(() => {
    setSelectedData({ ...selectedData, ...selectedInfo });
  }, [selectedInfo]);
  useEffect(() => {
    if (rowSelection) {
      setTableRowSelection({
        ...rowSelection,
        columnWidth: 48,
        onChange: (selectedRowKeys: Key[], selectedRows: any[], info: {
          type: RowSelectMethod;
        }) => {
          setSelectedData({
            selectedRowKeys,
            selectedRows,
          });
          rowSelection.onChange?.(selectedRowKeys, selectedRows, info);
        },
      });
      return;
    }
    if (!rowSelectionType && !rowSelection) {
      setTableRowSelection(undefined);
      return;
    }
    if (rowSelectionType && !rowSelection) {
      setTableRowSelection({
        type: rowSelectionType,
        selectedRowKeys: selectedData?.selectedRowKeys,
        columnWidth: 48,
        getCheckboxProps: getCheckboxProps ? (record: any) => {
          return getCheckboxProps(record);
        } : undefined,
        onSelect: (record: any, selected: boolean) => {
          const { selectedRowKeys = [], selectedRows = [] } = selectedData;
          const keyValue: string =
            typeof rowKey === 'string'
              ? record[rowKey]
              : rowKey?.(record) || record.key;
          if (selected) {
            if (rowSelectionType === 'checkbox') {
              setSelectedData({
                selectedRowKeys: [...selectedRowKeys, keyValue],
                selectedRows: [...selectedRows, record],
              });
            }
            if (rowSelectionType === 'radio') {
              setSelectedData({
                selectedRowKeys: [keyValue],
                selectedRows: [record],
              });
            }
          } else {
            setSelectedData({
              selectedRowKeys: selectedRowKeys.filter(v => v !== keyValue),
              selectedRows: selectedRows.filter(item => {
                const currentkeyValue: string =
                  typeof rowKey === 'string'
                    ? item[rowKey]
                    : rowKey?.(item) || item.key;
                return currentkeyValue !== keyValue;
              }),
            });
          }
        },
        onSelectAll: (selected, rows, changeRows) => {
          const { selectedRowKeys = [], selectedRows = [] } = selectedData;
          const changeRowsKeys: Key[] = changeRows.map(item => {
            const currentKey: string =
              typeof rowKey === 'string'
                ? item[rowKey]
                : rowKey?.(item) || item.key;
            return currentKey;
          });
          if (selected) {
            setSelectedData({
              selectedRowKeys: [...selectedRowKeys, ...changeRowsKeys],
              selectedRows: [...selectedRows, ...changeRows],
            });
          } else {
            setSelectedData({
              selectedRowKeys: selectedRowKeys.filter(
                v => !changeRowsKeys.includes(v),
              ),
              selectedRows: selectedRows.filter(item => {
                const currentkeyValue: string =
                  typeof rowKey === 'string'
                    ? item[rowKey]
                    : rowKey?.(item) || item.key;
                return !changeRowsKeys.includes(currentkeyValue);
              }),
            });
          }
        },
      });
    }
  }, [rowSelectionType, selectedData, rowSelection]);

  // 分页切换
  // const handleChange = (pagination: PaginationProps) => {
  //   updatePagination(pagination);
  // };
  // 表格大小改变
  const handleChangeSize = (val: tableSizeType) => {
    setTableSize(val);
  };
  /** 清空选中行 */
  const handleSelectEmpty = () => {
    setSelectedData({
      selectedRowKeys: [],
      selectedRows: [],
    });
    onSelectEmpty?.();
  };
  /** 分页参数改变 */
  const handlePaginationChange = (pagination: any) => {
    updatePagination(pagination);
    onPaginationChange?.(pagination);
  };
  // 对外接口
  if (actionRef) {
    actionRef.current = {
      reload: (resetPageIndex: boolean = false, params?: IObject) => {
        if (resetPageIndex) {
          resetTable('pagination', params);
        } else {
          refreshList(params);
        }
      },
      reloadDraftsCount: () => {
        toolbarRef.current?.reload();
      },
      reset: () => {
        initDable();
      },
      resetPagination: () => {
        resetPagination();
      },
      getDataSource: () => {
        return JSON.parse(JSON.stringify(currentDataSource));
      },
      setDataSource: (newDataSource: any) => {
        updateDataSource(Array.isArray(newDataSource) ? newDataSource : []);
      },
      resetFields: (params?: any) => {
        resetTable('all', params);
        searchFormRef.current?.resetFields?.();
      },
      submit: () => {
        searchFormRef.current?.submit();
      },
      setTableHeight: () => {
        setSuperTableHeight(true);
      },
      emptySelect: handleSelectEmpty,
      getTableParams: getTableParams,
      loading: () => {
        changeLoadingStatus(true);
      },
      unLoading: () => {
        changeLoadingStatus(false);
      },
      getSelectedInfo: () => {
        return selectedData;
      },
      reloadColumns: (newColumns) => {
        onChangeColumns([], newColumns || columns.slice());
      },
      form: searchFormRef.current,
    };
  }
  const handleSearch = async (values: any) => {
    const newValues = onBeforeSearch ? await onBeforeSearch(values) : values;
    if (newValues === false) return;
    updateListParams(values);
  };
  const classes = classnames('yb-table-content', contentClassName, {
    'yb-table-count-content': subTotalKeys || totalKeys,
  });
  const wrapperClasses = classnames('yb-table', wrapperClassName, {
    'yb-table-search': formItems && formItems.length,
  });
  const containerClasses = useMemo(() => {
    return classnames({
      'yb-table-container-small': tableSize === 'small',
      'yb-table-container-middle': tableSize === 'middle',
      'yb-table-container-large': tableSize === 'large',
    });
  }, [tableSize]);
  const newContentStyle: CSSProperties = useMemo(() => {
    const newStyle = { ...contentStyle };
    if (hideContainer) newStyle.display = 'none';
    return newStyle;
  }, [hideContainer, contentStyle]);
  const components = useMemo(() => {
    if (resizable) {
      return {
        header: {
          cell: ResizableTitle,
        },
      }
    }
  }, [ resizable ]);
  const tableColumns = useMemo(() => {
    return columns.filter(v => !v.isHide);
  }, [columns]);
  return (
    <div className={wrapperClasses} id={tableId} style={wrapperStyle}>
      {formItems && !!formItems.length && (
        <SuperForm.TableForm
          testId={testId}
          formItems={formItems}
          onFinish={handleSearch}
          onReset={initDable}
          showBorder={showSearchFormBorder}
          actionRef={searchFormRef}
          formType="search"
          filterEmpty={false}
          highWidth={highWidth}
          operationProps={operationProps}
          defaultLineNumber={defaultLineNumber}
          defaultLabelCol={defaultLabelCol}
          maxItemLength={maxItemLength}
          highSearch={highSearch}
        />
      )}
      {headerTip}
      <div className={classes} style={newContentStyle}>
        {
          (headOperation || toolBar || headOperationAfter) && (
            <TableToolbar
              toolbarRef={toolbarRef}
              headOperation={headOperation}
              size={tableSize}
              toolBar={toolBar}
              columns={outColumns}
              headOperationAfter={headOperationAfter}
              dataSource={currentDataSource}
              copyColumns={copyColumns}
              selectedRows={selectedData.selectedRows}
              defaultUnrepeated={defaultUnrepeated}
              chilrenKey={chilrenKey}
              onRefresh={refreshList}
              onChangeSize={handleChangeSize}
              onChangeColumns={onChangeColumns}
              getTableParams={getTableParams}
              onSelectEmpty={handleSelectEmpty}
              toolBarButtons={toolBarButtons}
              toolBarFooterContent={toolBarFooterContent}
              isMainSubTable={isMainSubTable}
              isMergeTable={isMergeTable}
              rowSelectionType={rowSelectionType}
              showHelpIcon={showHelpIcon}
            />
          )
        }
        <div ref={containerRef} className={containerClasses}>
          <Table
            ref={tableRef}
            loading={loading || intLoading}
            columns={tableColumns}
            bordered={bordered}
            dataSource={currentDataSource}
            pagination={false}
            rowKey={rowKey}
            size='small'
            rowHoverable={rowHoverable}
            rowSelection={tableRowSelection}
            expandable={expandable}
            className={oddEvenLine ? 'odd-even-line' : undefined}
            rowClassName={rowClassName}
            components={components}
            virtual={virtual}
            scroll={
              hideScroll
                ? undefined
                : scroll ?? {
                    scrollToFirstRowOnChange: true,
                    y: tableHeight,
                  }
            }
            showHeader={showHeader}
            onRow={onRow}
            summary={(currentData: any) => {
              if (summary) {
                return summary(currentData);
              }
              return (
                <>
                  <TableSubTotal
                    columns={columns}
                    pageData={currentDataSource}
                    subTotalKeys={subTotalKeys}
                    subTotalIndex={subTotalIndex}
                    subTotalText={subTotalText}
                    hasSelect={!!rowSelection}
                    tableRef={containerRef}
                  />
                  <TableTotal
                    columns={columns}
                    totalApi={totalApi}
                    totalApiParams={totalApiParams}
                    totalDataKey={totalDataKey}
                    totalIndex={totalIndex}
                    totalKeys={totalKeys}
                    totalText={totalText}
                    hasSelect={!!rowSelection}
                    totalRef={totalRef}
                    totalData={totalData}
                  />
                </>
              );
            }}
          />
        </div>
        {props.pagination !== false && (
          <TablePagination
            {...pagination}
            total={total}
            onChange={handlePaginationChange}
          />
        )}
      </div>
    </div>
  );
};

SuperTable.defaultProps = {
  dataKey: 'list',
  sceneType: 'page',
  bordered: false,
  autoPage: true,
  resizable: true,
  oddEvenLine: false,
  totalKey: 'total',
  delay: 100,
  operation: ['detail', 'edit', 'delete'],
  showHeader: true,
  autoHeight: true,
  highSearch: false,
  toolBar: ['refresh', 'copy'],
  autoMinHeight: 200,
  isMainSubTable: false,
  footerHeight: 0,
  drafts: false,
  showSearchFormBorder: false,
  hideScroll: false,
  hideContainer: false,
  minVirtualTotal: false,
};

export default SuperTable;
